import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { filter, take, switchMap } from 'rxjs';
import { UpdateEventNavigatorGQL } from 'src/generated/graphql.generated';
import { transform } from 'sucrase';

import { generateMutationContext } from '../../../core/auth/session';
import { quickJsActions } from '../../../quickjs/quickjs.actions';
import { QuickJsService } from '../../../quickjs/quickjs.service';
import { PermissionService } from '../../../services/permission.service';
import {
    NAVIGATOR_FINISHED_STEP,
    NAVIGATOR_STARTED_SENTINEL,
    navigatorStepMoveTimings,
    StepTiming,
} from '../../job-helpers';
import { selectJob, selectJobId, selectNavigatorUrlForEvent } from '../../job-tool.reducer';

import { WorkOrdersActions } from '../job-workorders.actions';

interface StepData {
    id: string;
    title: string;
    maxTime: number;       // seconds — scheduled max (editable)
    customProps: Record<string, any>;
    /** Actual duration in seconds for completed steps (startedAt → completedAt) */
    actualSeconds: number | null;
    startedAt: number | null;  // epoch ms
    completedAt: number | null;// epoch ms
    isCompleted: boolean;
}

interface StepOption {
    label: string;
    value: string;
}

@Component({
    selector: 'app-update-navigator-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        InputNumberModule,
        DropdownModule,
        CheckboxModule,
        ProgressSpinnerModule,
        TooltipModule,
    ],
    styles: [`
        .state-msg {
            display: flex; align-items: center; justify-content: center;
            gap: 0.75rem; padding: 2rem; color: #666;
        }
        .state-msg.error { color: #c00; }
        /* Sits directly above the footer, so it needs its own spacing and a readable
           left-aligned wrap -- .state-msg is otherwise used for centred full-panel states. */
        .reset-error {
            text-align: left;
            font-size: 0.85rem;
            line-height: 1.4;
            padding: 0.5rem 0 0;
        }

        /* ── Current step ── */
        .current-step-field { margin-bottom: 1.5rem; }
        .field-label {
            display: block; font-size: 0.8rem; font-weight: 700;
            text-transform: uppercase; letter-spacing: 0.04em;
            color: #555; margin-bottom: 0.4rem;
        }
        .save-indicator {
            display: inline-flex; align-items: center; gap: 0.3rem;
            font-size: 0.78rem; color: #2d6a2d; margin-top: 0.3rem;
            min-height: 1.2rem;
        }
        .save-indicator.saving { color: #888; }

        /* ── Step list ── */
        .step-list { display: flex; flex-direction: column; gap: 0.4rem; margin-bottom: 1rem; }
        .step-row {
            display: flex; align-items: center; gap: 0.75rem;
            padding: 0.65rem 1rem;
            border: 1px solid #dee2e6; border-radius: 8px;
            cursor: pointer; background: white;
            transition: background 0.12s, border-color 0.12s;
        }
        .step-row:hover { background: #f8f9fa; border-color: #adb5bd; }
        .step-row.is-current { border-color: #2d6a2d; background: #f0f7f0; }
        .step-num { font-size: 0.78rem; color: #aaa; width: 1.4rem; flex-shrink: 0; text-align: right; }
        .step-title { flex: 1; font-size: 0.95rem; color: #222; }
        .step-meta { font-size: 0.78rem; color: #888; flex-shrink: 0; text-align: right; line-height: 1.5; }
        .step-actual-time { font-weight: 600; }
        .step-actual-time.over { color: #c0392b; }
        .step-actual-time.under { color: #2d6a2d; }
        .step-start-time { font-size: 0.72rem; color: #aaa; }
        .current-chip {
            font-size: 0.7rem; background: #2d6a2d; color: white;
            padding: 0.1rem 0.5rem; border-radius: 10px; flex-shrink: 0;
        }
        /* ── Step editor ── */
        .editor-back {
            display: inline-flex; align-items: center; gap: 0.4rem;
            background: none; border: none; cursor: pointer;
            color: #555; font-size: 0.9rem; padding: 0 0 1rem 0;
        }
        .editor-back:hover { color: #222; }
        .editor-title { font-size: 1.1rem; font-weight: 700; margin: 0 0 1.25rem; color: #1a1a1a; }
        .field { margin-bottom: 1.25rem; }
        .time-hint { font-size: 0.8rem; color: #888; margin-top: 0.3rem; }
        .time-row {
            display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem;
            margin-bottom: 1.25rem;
        }
        .time-block label { display: block; font-size: 0.72rem; color: #888; margin-bottom: 0.2rem; }
        .time-block .time-val { font-size: 0.95rem; font-weight: 500; color: #222; }
        .cascade-note {
            font-size: 0.78rem; color: #666;
            background: #fff8e1; border-left: 3px solid #f59e0b;
            padding: 0.4rem 0.6rem; border-radius: 0 4px 4px 0;
            margin-top: 0.4rem; margin-bottom: 1rem;
        }
        .prop-grid { display: flex; flex-direction: column; gap: 0.6rem; }
        .prop-row { display: flex; align-items: center; gap: 0.75rem; }
        .prop-key { flex: 1; font-size: 0.9rem; color: #444; }
        .prop-val { flex: 2; }

        /* ── Footer ── */
        .footer {
            display: flex; justify-content: flex-end; gap: 0.75rem;
            padding-top: 1rem; border-top: 1px solid #dee2e6; margin-top: 0.5rem;
        }
        .footer-left { flex: 1; }
    `],
    template: `
        <!-- Loading -->
        <div class="state-msg" *ngIf="loading">
            <p-progressSpinner strokeWidth="3" [style]="{width:'28px',height:'28px'}"></p-progressSpinner>
            Loading navigator steps...
        </div>

        <!-- Error -->
        <div class="state-msg error" *ngIf="!loading && loadError">
            <i class="pi pi-exclamation-circle"></i> {{ loadError }}
        </div>

        <!-- Main list view -->
        <ng-container *ngIf="!loading && !loadError && !selectedStep">

            <!-- Current step field -->
            <div class="current-step-field">
                <label class="field-label">Current Step</label>

                <!-- Completed state: read-only badge -->
                <div *ngIf="jobDone"
                     style="display:flex;align-items:center;gap:0.5rem;padding:0.6rem 0.75rem;
                            border:1px solid #dee2e6;border-radius:6px;background:#f8f9fa;color:#555">
                    <i class="pi pi-check-circle" style="color:#1a5276"></i>
                    <span style="font-weight:600;color:#1a5276">Completed</span>
                </div>

                <!-- In-progress: editable dropdown, ops only -->
                <p-dropdown *ngIf="!jobDone && (canEditNavigator$ | async)"
                    [options]="stepOptions"
                    [(ngModel)]="currentStepId"
                    (ngModelChange)="onCurrentStepChange($event)"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Not started"
                    styleClass="w-full">
                </p-dropdown>

                <!-- Read-only for anyone without the ops permission. Shows the same fact, without
                     offering to change it — a disabled dropdown reads as broken. -->
                <div *ngIf="!jobDone && (canEditNavigator$ | async) !== true"
                     style="padding:0.5rem 0;font-weight:600;color:#1a1a1a">
                    {{ currentStepLabel }}
                </div>

                <div class="save-indicator" [class.saving]="saving">
                    <ng-container *ngIf="saving"><i class="pi pi-spin pi-spinner"></i> Saving...</ng-container>
                    <ng-container *ngIf="!saving && lastSaved"><i class="pi pi-check"></i> Saved</ng-container>
                </div>
            </div>

            <!-- Step list — click to edit timing & properties -->
            <label class="field-label">
                Steps
                <span style="font-weight:400;text-transform:none;letter-spacing:0;color:#aaa;margin-left:0.4rem">(tap to edit)</span>
            </label>
            <div class="step-list">
                <div *ngFor="let step of steps; let i = index"
                     class="step-row"
                     [class.is-current]="step.id === currentStepId"
                     (click)="openStep(step)">
                     <span class="step-num">{{ i + 1 }}.</span>
                    <span class="step-title">{{ step.title }}</span>
                    <span class="current-chip" *ngIf="step.id === currentStepId">Current</span>
                    <span class="step-meta">
                        <!-- Start time clock -->
                        <div class="step-start-time" *ngIf="step.startedAt">
                            {{ formatClock(step.startedAt) }}
                        </div>
                        <!-- Completed: actual / max both in minutes -->
                        <ng-container *ngIf="step.isCompleted && step.actualSeconds !== null">
                            <div class="step-actual-time"
                                 [class.over]="step.actualSeconds > step.maxTime"
                                 [class.under]="step.actualSeconds <= step.maxTime">
                                {{ formatMins(step.actualSeconds) }}
                            </div>
                            <div style="color:#bbb">/ {{ formatMins(step.maxTime) }}</div>
                        </ng-container>
                        <!-- Not yet reached: max only -->
                        <ng-container *ngIf="!step.isCompleted || step.actualSeconds === null">
                            <span>{{ formatMins(step.maxTime) }}</span>
                        </ng-container>
                    </span>
                    <i class="pi pi-chevron-right" style="color:#ccc;font-size:0.8rem"></i>
                </div>
            </div>

            <div class="state-msg error reset-error" *ngIf="resetError">{{ resetError }}</div>

            <div class="footer">
                <div class="footer-left">
                    <p-button
                        *ngIf="canEditNavigator$ | async"
                        label="Reset Navigator"
                        styleClass="p-button-text p-button-danger"
                        icon="pi pi-refresh"
                        pTooltip="Archives the current navigator data to the job's comments, then clears it"
                        (click)="reset()">
                    </p-button>
                </div>
                <p-button label="Close" styleClass="p-button-text" (click)="close()"></p-button>
            </div>
        </ng-container>

        <!-- Step editor — saves on "Save Step" -->
        <ng-container *ngIf="!loading && !loadError && selectedStep">
            <button class="editor-back" (click)="closeEditor()">
                <i class="pi pi-arrow-left"></i> All Steps
            </button>

            <h3 class="editor-title">{{ selectedStep.title }}</h3>

            <!-- Start / End times (read-only reference) -->
            <div class="time-row" *ngIf="selectedStep.startedAt || selectedStep.completedAt">
                <div class="time-block" *ngIf="selectedStep.startedAt">
                    <label>Started</label>
                    <div class="time-val">{{ formatClock(selectedStep.startedAt) }}</div>
                </div>
                <div class="time-block" *ngIf="selectedStep.completedAt">
                    <label>Ended</label>
                    <div class="time-val">{{ formatClock(selectedStep.completedAt) }}</div>
                </div>
            </div>

            <!-- Actual duration — only for completed steps -->
            <div class="field" *ngIf="selectedStep.isCompleted">
                <label class="field-label">Actual Duration</label>
                <p-inputNumber
                    [(ngModel)]="editActualMinutes"
                    [min]="0" [max]="999" [step]="1" [showButtons]="true"
                    suffix=" min"
                    styleClass="w-full">
                </p-inputNumber>
                <div class="time-hint">
                    Was {{ formatMins(selectedStep.actualSeconds ?? 0) }} · Max {{ formatMins(selectedStep.maxTime) }}
                </div>
                <div class="cascade-note" *ngIf="hasSubsequentSteps">
                    <i class="pi pi-info-circle mr-1"></i>
                    Changing this will shift start times for subsequent steps.
                </div>
            </div>

            <div class="field">
                <label class="field-label">Max Time (budget)</label>
                <p-inputNumber
                    [(ngModel)]="editMaxMinutes"
                    [min]="0" [step]="1" [showButtons]="true"
                    suffix=" min"
                    styleClass="w-full">
                </p-inputNumber>
            </div>

            <div class="field" *ngIf="selectedStepProps.length > 0">
                <label class="field-label">Properties</label>
                <div class="prop-grid">
                    <div class="prop-row" *ngFor="let prop of selectedStepProps; trackBy: trackProp">
                        <span class="prop-key">{{ prop.key }}</span>
                        <input *ngIf="prop.type === 'string'"
                               class="prop-val p-inputtext p-component"
                               [(ngModel)]="selectedStep.customProps[prop.key]" />
                        <p-inputNumber *ngIf="prop.type === 'number'"
                               styleClass="prop-val"
                               [(ngModel)]="selectedStep.customProps[prop.key]">
                        </p-inputNumber>
                        <p-checkbox *ngIf="prop.type === 'boolean'"
                               [binary]="true"
                               [(ngModel)]="selectedStep.customProps[prop.key]">
                        </p-checkbox>
                    </div>
                </div>
            </div>

            <div class="footer">
                <p-button label="Back" styleClass="p-button-text" (click)="closeEditor()"></p-button>
                <p-button
                    label="Save Step"
                    icon="pi pi-check"
                    [loading]="saving"
                    (click)="saveStep()">
                </p-button>
            </div>
        </ng-container>
    `,
})
export class UpdateNavigatorDialogComponent implements OnInit {

    loading = true;
    loadError: string | null = null;
    saving = false;
    lastSaved = false;

    steps: StepData[] = [];
    stepOptions: StepOption[] = [];
    currentStepId: string | null = null;
    selectedStep: StepData | null = null;
    /**
     * Editable properties of `selectedStep`, computed once in `openStep()`.
     *
     * This MUST NOT be a method called from the template. `*ngFor` over a method
     * returning a freshly-built array gives every item a new object identity on
     * every change-detection pass, so the differ tears down and recreates each
     * row's `p-inputNumber` / `p-checkbox`. Each new `ngModel` schedules a
     * microtask, which keeps the zone unstable and triggers another pass — an
     * unbreakable microtask loop that hard-locks the tab (the browser never gets
     * a turn, so even reloading is impossible). See issue #11.
     */
    selectedStepProps: { key: string; type: 'string' | 'number' | 'boolean' }[] = [];
    /** Bound to the actual duration input in the step editor (minutes) */
    editActualMinutes: number = 0;
    /** Bound to the max time input in the step editor (minutes) */
    editMaxMinutes: number = 0;

    private eventId: string;
    /** Full navigatorData object — includes both step_* overrides and steps timing */
    private pendingData: Record<string, any> = {};
    /** The event's own lifecycle status, which gates whether a reset is allowed (#62). */
    private eventStatus: string | null = null;

    /** Shown under the Reset button. Covers both the #62 refusal and a failed archive. */
    resetError: string | null = null;

    get jobDone(): boolean { return this.currentStepId === 'completed'; }

    get hasSubsequentSteps(): boolean {
        if (!this.selectedStep) return false;
        const idx = this.steps.findIndex(s => s.id === this.selectedStep!.id);
        return idx >= 0 && idx < this.steps.length - 1;
    }

    constructor(
        private store: Store,
        private http: HttpClient,
        private quickJs: QuickJsService,
        private updateEventNavigatorGQL: UpdateEventNavigatorGQL,
        private ref: DynamicDialogRef,
        private config: DynamicDialogConfig,
    ) {}

    ngOnInit() {
        this.eventId = this.config.data.eventId;

        this.store.select(selectJob).pipe(
            filter(job => !!job),
            take(1),
            switchMap(job => {
                const event = job?.events?.find(e => e.id === this.eventId);
                if (!event) throw new Error('Event not found');

                this.currentStepId = event.navigatorCurrentStep ?? null;
                this.eventStatus = event.status ?? null;
                try {
                    this.pendingData = event.navigatorData
                        ? JSON.parse(event.navigatorData) : {};
                } catch { this.pendingData = {}; }

                return this.store.select(selectNavigatorUrlForEvent(event)).pipe(
                    take(1),
                    switchMap(url => {
                        if (!url) throw new Error(`No navigator URL for event type "${event.type}"`);
                        return this.http.get(url, { responseType: 'text' }).pipe(
                            switchMap(async script => {
                                const cjsCode = transform(script, {
                                    transforms: ['typescript', 'jsx', 'imports'],
                                    jsxPragma: 'h',
                                    jsxFragmentPragma: 'Fragment',
                                }).code;
                                const compiled = `(function() { const exports = {}; ${cjsCode}; Object.assign(globalThis, exports); })();`;
                                await this.quickJs.loadNavigatorScript(compiled);
                                return this.quickJs.getNavigatorTree(
                                    { locations: {}, customer: {}, dock_start: { travelTime: 0 } },
                                    {},
                                );
                            }),
                        );
                    }),
                );
            }),
        ).subscribe({
            next: (tree) => {
                const initialStepId: string | undefined = this.config.data.initialStepId;
                // Exclude the sentinel 'complete' step — it has no timing to edit
                const stepNodes = (tree?.children ?? [])
                    .filter((c: any) => c.type === 'Step' && c.props?.id !== 'complete');
                const jobDone = this.currentStepId === 'completed';
                const currentIdx = jobDone
                    ? stepNodes.length // all steps completed
                    : stepNodes.findIndex((s: any) => s.props?.id === this.currentStepId);
                const stepsTimingData: Record<string, any> = this.pendingData?.steps ?? {};

                this.steps = stepNodes.map((s: any, i: number) => {
                    const id = s.props?.id ?? '';
                    const override = this.pendingData?.[`step_${id}`] ?? {};
                    const timingData = stepsTimingData[id] ?? {};
                    // When job is done all steps are completed; otherwise steps before current
                    const isCompleted = jobDone || (currentIdx >= 0 && i < currentIdx);

                    const startedAt: number | null = timingData.startedAt ?? null;
                    const completedAt: number | null = timingData.completedAt ?? null;
                    const actualSeconds = (isCompleted && startedAt && completedAt)
                        ? Math.floor((completedAt - startedAt) / 1000)
                        : null;

                    return {
                        id,
                        title: s.props?.title ?? id,
                        maxTime: override.maxTime ?? s.props?.maxTime ?? 0,
                        customProps: this._extractEditableProps(s.props ?? {}, override),
                        actualSeconds,
                        startedAt,
                        completedAt,
                        isCompleted,
                    };
                });
                this.stepOptions = this.steps.map((s, i) => ({
                    label: `${i + 1}. ${s.title}`,
                    value: s.id,
                }));
                this.loading = false;

                // Auto-open a specific step if requested (e.g. from the status component)
                if (initialStepId) {
                    const targetStep = this.steps.find(s => s.id === initialStepId);
                    if (targetStep) this.openStep(targetStep);
                }
            },
            error: err => {
                this.loadError = err?.message ?? 'Failed to load navigator script';
                this.loading = false;
                console.error(err);
            },
        });
    }

    /**
     * Whether this user may change or reset a crew's navigator step.
     *
     * **This is a UI guard, not enforcement.** `mutationUpdateEventNavigator` guards on
     * `calendarEvents.edit` — the *same* permission the crew needs to run the navigator at all, on
     * the *same* mutation their own step confirms go through. The server therefore cannot tell an
     * ops override from a crew advance, and anyone who can reach the API can still call it. Closing
     * that needs a real permission and is #21's "run/edit split", deferred to its own MR.
     *
     * **`dispatch.viewDispatch` is a proxy for "works from the office", not the eventual answer.**
     * Chosen from the actual role data rather than guessed: no crew role holds any `dispatch`
     * permission, while managers do. The obvious candidates are worse — crew leads hold
     * `jobs\.update` (that is #33), and the only `calendarEvents.edit` restrictions are
     * `denyPast` / `denyFuture` / `modifyLockedEvents`, none of which means "manager".
     *
     * When #21 lands, re-point this at the real permission; the guard itself stays as
     * belt-and-braces, hiding what the server would then reject.
     *
     * **Do not name that permission `calendarEvents.*`** — crew leads hold the wildcard
     * `calendarEvents\..*`, so anything in that namespace is auto-granted to them. `navigator.edit`
     * or similar.
     */
    private readonly permissionService = inject(PermissionService);
    // Requirement: #21 - run/edit split; server-side enforcement pending.
    //
    // This gate is UI-only. `mutationUpdateEventNavigator` still guards on `calendarEvents.edit`,
    // which is the same permission the crew needs to run the navigator at all — so any crew user
    // who can reach the API can still override a step or reset a navigator. Hiding the control is
    // not enforcing it, and a hidden control reads as an enforced one to the next person here.
    // `dispatch.viewDispatch` is a stand-in manager discriminator (no crew role holds any
    // `dispatch` permission); the real fix is a `navigator.edit` permission plus a separate ops
    // mutation, tracked as #21 in docs/crew-navigator.md.
    canEditNavigator$ = this.permissionService.watchPermission('dispatch.viewDispatch');

    /** The current step's label, for the read-only view shown without the permission above. */
    get currentStepLabel(): string {
        return this.stepOptions?.find(o => o.value === this.currentStepId)?.label ?? 'Not started';
    }

    /**
     * Fires immediately when the dropdown changes — persists the new current step together with the
     * timings that go with it (#53, `navigatorStepMoveTimings`).
     *
     * Reads the event as the store holds it now, not this dialog's copy from when it opened: the crew
     * may have advanced since, and the rule depends on where they really are.
     */
    onCurrentStepChange(stepId: string) {
        const event = this.store.selectSignal(selectJob)()?.events?.find(e => e.id === this.eventId);
        let stored: Record<string, any> = {};
        try { stored = event?.navigatorData ? JSON.parse(event.navigatorData) : {}; } catch { /* treat as empty */ }

        const timings = navigatorStepMoveTimings(
            this.steps.map(s => s.id),
            stored.steps ?? {},
            event?.navigatorCurrentStep ?? this.currentStepId,
            stepId,
            Date.now(),
        );

        this._mutate(
            { currentStep: stepId, ...(timings ? { navigatorData: JSON.stringify(timings) } : {}) },
            () => this._rebuildStepsFromPendingData(),
        );
    }

    /**
     * Rebuilds every row from the stored run after a step change. Unlike the cascade refresh below,
     * nothing falls back to the row's old value: a step the change cleared must show as cleared.
     */
    private _rebuildStepsFromPendingData() {
        const stepsTimingData: Record<string, Partial<StepTiming>> = this.pendingData.steps ?? {};
        const jobDone = this.currentStepId === NAVIGATOR_FINISHED_STEP;
        const currentIdx = this.steps.findIndex(s => s.id === this.currentStepId);
        this.steps = this.steps.map((step, i) => {
            const startedAt = stepsTimingData[step.id]?.startedAt ?? null;
            const completedAt = stepsTimingData[step.id]?.completedAt ?? null;
            const isCompleted = jobDone || (currentIdx >= 0 && i < currentIdx);
            const actualSeconds = (isCompleted && startedAt && completedAt)
                ? Math.floor((completedAt - startedAt) / 1000)
                : null;
            return { ...step, startedAt, completedAt, actualSeconds, isCompleted };
        });
    }

    openStep(step: StepData) {
        this.selectedStep = { ...step, customProps: { ...step.customProps } };
        this.selectedStepProps = this.editableProps(this.selectedStep);
        this.editActualMinutes = step.actualSeconds !== null
            ? Math.round(step.actualSeconds / 60)
            : Math.round(step.maxTime / 60);
        this.editMaxMinutes = Math.round(step.maxTime / 60);
    }

    /** Leaves the step editor and returns to the list. */
    closeEditor() {
        this.selectedStep = null;
        this.selectedStepProps = [];
    }

    trackProp(_index: number, prop: { key: string }) { return prop.key; }

    editableProps(step: StepData): { key: string; type: 'string' | 'number' | 'boolean' }[] {
        return Object.entries(step.customProps)
            .filter(([, v]) => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')
            .map(([key, v]) => ({ key, type: typeof v as 'string' | 'number' | 'boolean' }));
    }

    /** Saves step timing/props immediately and returns to the list. */
    saveStep() {
        if (!this.selectedStep) return;

        const step = this.selectedStep;
        const idx = this.steps.findIndex(s => s.id === step.id);
        // The row as loaded; `selectedStep` is the editor's copy.
        const loaded = idx >= 0 ? this.steps[idx] : null;

        // ── Update step_* override (maxTime in seconds + custom props) ──
        const maxTimeSeconds = this.editMaxMinutes * 60;
        const settingsChanged = !loaded
            || loaded.maxTime !== maxTimeSeconds
            || JSON.stringify(loaded.customProps) !== JSON.stringify(step.customProps);
        step.maxTime = maxTimeSeconds; // keep local copy in sync
        const stepKey = `step_${step.id}`;
        this.pendingData[stepKey] = {
            maxTime: maxTimeSeconds,
            ...step.customProps,
        };

        // What this save changes, and nothing else (#48): the server merges it into the stored run.
        // `pendingData` is this dialog's copy from when it opened, so sending it whole wrote that copy
        // back over every step the crew had saved since — a dialog left open for an hour erased an
        // hour of the crew's work.
        // The settings go only when they changed, so a duration-only edit does not also record a
        // settings change in the event's history (#88).
        const patch: Record<string, any> = settingsChanged ? { [stepKey]: this.pendingData[stepKey] } : {};

        // ── Update actual duration if this is a completed step ──
        if (step.isCompleted && step.startedAt !== null) {
            const newActualSeconds = this.editActualMinutes * 60;
            const newCompletedAt = step.startedAt + newActualSeconds * 1000;

            // Update steps timing data
            const stepsData = { ...(this.pendingData.steps ?? {}) };
            stepsData[step.id] = {
                ...(stepsData[step.id] ?? {}),
                startedAt: step.startedAt,
                completedAt: newCompletedAt,
            };

            // Cascade: shift subsequent steps' startedAt if they were sequential
            this._cascadeTimings(stepsData, idx, newCompletedAt);

            // The edited step and the ones the cascade moved: both assign a new entry, so an entry
            // that is not the one this dialog loaded is a changed one.
            const loadedSteps: Record<string, any> = this.pendingData.steps ?? {};
            patch['steps'] = Object.fromEntries(
                Object.entries(stepsData).filter(([id, timing]) => timing !== loadedSteps[id]),
            );

            this.pendingData = { ...this.pendingData, steps: stepsData };

            // Update the local steps array so the list view refreshes
            if (idx >= 0) {
                this.steps[idx] = {
                    ...step,
                    actualSeconds: newActualSeconds,
                    completedAt: newCompletedAt,
                };
                // Also refresh subsequent steps with new startedAt/completedAt
                this._refreshStepsFromPendingData();
            }
        } else if (idx >= 0) {
            this.steps[idx] = { ...step };
        }

        this._mutate({ navigatorData: JSON.stringify(patch) }, () => {
            this.closeEditor();
        });
    }

    /**
     * Cascade timing changes from stepIdx+1 onward.
     * Mirrors the logic in NavigatorStatusComponent._cascadeTimings.
     */
    private _cascadeTimings(
        stepsData: Record<string, any>,
        fromIdx: number,
        newEndTime: number,
    ) {
        for (let i = fromIdx + 1; i < this.steps.length; i++) {
            const id = this.steps[i].id;
            const prevId = this.steps[i - 1].id;
            const prevData = stepsData[prevId];
            const thisData = stepsData[id];

            if (!thisData?.startedAt || !prevData?.completedAt) break;

            const gap = Math.abs(thisData.startedAt - prevData.completedAt);
            if (gap > 5000) break; // >5s manual gap — stop cascading

            const duration = thisData.completedAt
                ? thisData.completedAt - thisData.startedAt
                : null;

            stepsData[id] = {
                ...thisData,
                startedAt: newEndTime,
                ...(duration !== null && { completedAt: newEndTime + duration }),
            };

            newEndTime = stepsData[id].completedAt ?? newEndTime;
        }
    }

    /** Refresh the steps array with updated timing from pendingData after a cascade. */
    private _refreshStepsFromPendingData() {
        const stepsTimingData: Record<string, any> = this.pendingData.steps ?? {};
        const currentIdx = this.steps.findIndex(s => s.id === this.currentStepId);
        this.steps = this.steps.map((step, i) => {
            const timingData = stepsTimingData[step.id] ?? {};
            const isCompleted = currentIdx >= 0 ? i < currentIdx : false;
            const startedAt: number | null = timingData.startedAt ?? step.startedAt;
            const completedAt: number | null = timingData.completedAt ?? step.completedAt;
            const actualSeconds = (isCompleted && startedAt && completedAt)
                ? Math.floor((completedAt - startedAt) / 1000)
                : step.actualSeconds;
            return { ...step, startedAt, completedAt, actualSeconds };
        });
    }

    /**
     * Clears the navigator. The server archives the run to the job's comments before it wipes it
     * (#98) — for every caller, not only this dialog — and refuses the reset if the archive fails.
     *
     * **Refuses on a completed event (#62).** Reset leaves `navigatorCurrentStep = 'started'`
     * while the event keeps `status: 'completed'`, and that pair matches none of the crew-nav
     * row's three branches — the row renders empty and reads as a broken button. Rather than
     * teach the row to render that state, or write the event's status from here (it feeds the
     * job stage pipeline and the revenue reports' `eventStatuses` filter), the reset refuses and
     * names the way forward. Working backwards from why anyone resets — wrong event, a test run
     * on a real job, garbage timings polluting reports — the goal is always to remove data, never
     * to re-run the navigator on a finished move.
     */
    reset() {
        this.resetError = null;

        if (this.eventStatus === 'completed') {
            this.resetError = 'This event is completed. Change its status to Booked or Confirmed '
                + 'before resetting the navigator.';
            return;
        }

        // The data this dialog showed, sent as the compare-and-swap guard: if the crew saved a step
        // since, the server refuses rather than archiving and wiping something ops has not seen.
        const job = this.store.selectSignal(selectJob)();
        const shown = job?.events?.find(e => e.id === this.eventId)?.navigatorData ?? '';
        this._performReset(shown);
    }

    /**
     * The archive-and-wipe request.
     *
     * `expected` is the `navigatorData` this dialog showed, sent as a compare-and-swap guard. If
     * the crew saved a step since, the stored value no longer matches and the server refuses. A
     * refusal costs one retry — the reopened dialog reads the fresh value — and never costs data.
     *
     * **Nothing local is cleared until the server confirms.** These dispatches used to run before
     * the mutation, so a refused or failed reset left the dialog, the workorders row and a mounted
     * navigator all showing a reset that had not happened.
     */
    private _performReset(expected: string) {
        // `resetNavigator` owns the whole shape server-side: it removes every flattened
        // `navigator_*` mirror, blanks the JSON and sets the step to 'started', preserving
        // `navigatorUrl` so the Start button still resolves a script. Sending `navigatorData: '{}'`
        // from here — as this did before — left ~30 mirrors behind, which outlived the JSON and
        // kept feeding dynamic reports (#49), while the resume found no `startedAt` and stamped
        // "now" (#56). The store is still patched to null so the crew-nav-row stays visible.
        this._mutate(
            { resetNavigator: true, expectedNavigatorData: expected },
            () => {
                this.pendingData = {};
                this.currentStepId = null;

                // Clear the quickjs slice as well as the jobs slice. `NavigatorRoot` restores from
                // `quickJsState.navigatorCurrentStepId`, not from the event, so patching only the
                // jobs slice left the button label correct while the navigator still reopened on
                // the old step — right up until a page reload dropped this slice (#26).
                this.store.dispatch(quickJsActions.navigatorReset({ eventId: this.eventId }));
            },
            null, // store navigatorCurrentStep = null, not 'started'
            (err) => {
                // The server names each refusal it can raise — a completed event, data that moved
                // since the dialog opened, a failed archive. Show what it said rather than a generic
                // failure, because each has a different way forward.
                this.resetError = err?.message?.replace(/^GraphQL error:\s*/i, '')
                    || 'The reset failed and nothing was changed. Please try again.';
            },
        );
    }

    close() {
        this.ref.close(null);
    }

    formatTime(seconds: number): string {
        if (!seconds) return '—';
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return m > 0 ? `${m}m${s > 0 ? ' ' + s + 's' : ''}` : `${s}s`;
    }

    /** Format duration as decimal minutes, e.g. 5m, 12.5m */
    formatMins(seconds: number | null | undefined): string {
        if (seconds == null || seconds === 0) return '0m';
        const m = seconds / 60;
        return m === Math.floor(m) ? `${m}m` : `${m.toFixed(1)}m`;
    }

    /** Format epoch-ms as clock time, e.g. "2:34 PM" */
    formatClock(epochMs: number | null | undefined): string {
        if (!epochMs) return '—';
        return new Date(epochMs).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }

    /** Central mutation caller — sets saving state, patches store, shows saved indicator.
     *  @param storeCurrentStep Override what gets written to the NgRx store (defaults to fields.currentStep).
     *                          Pass null to clear the step in the store without hiding the crew-nav-row.
     */
    private _mutate(
        fields: {
            currentStep?: string;
            navigatorData?: string;
            navigatorUrl?: string;
            resetNavigator?: boolean;
            expectedNavigatorData?: string;
        },
        onSuccess?: () => void,
        storeCurrentStep?: string | null,
        onError?: (err: any) => void,
    ) {
        this.saving = true;
        this.lastSaved = false;

        // Capture jobId synchronously before the async mutation so we can
        // dispatch the store patch in the success handler. `selectSignal` is a plain
        // `computed()` over the store state, so reading it here needs no subscription
        // and no injection context.
        const jobId = this.store.selectSignal(selectJobId)();

        // `generateMutationContext()` marks this write as ours, so its echo arrives as
        // `localJobUpdated`. That matters now that the navigator's remote-override effect is the only
        // way outside state can move a mounted crew: without the context this write comes back as
        // *remote* and the crew would be moved by our own echo as well as by the explicit dispatch
        // below — the accident this dialog used to rely on.
        this.updateEventNavigatorGQL.mutate({
            eventId: this.eventId,
            ...fields,
            // Every write from this dialog is an ops correction: its history entries say so (#88).
            opsCorrection: true,
        }, { context: generateMutationContext() }).subscribe({
            next: (result) => {
                this.saving = false;
                this.lastSaved = true;

                // Immediately patch the NgRx store so the job in memory (and in the
                // localStorage persist:v2.1 snapshot) reflects the new navigator state.
                // This bypasses the WebSocket round-trip and ensures the dialog
                // shows fresh data even after a page reload.
                if (jobId) {
                    const returned = result.data?.updateEventNavigator;
                    // storeCurrentStep=undefined → use returned/fields value; storeCurrentStep=null → clear in store
                    const navigatorCurrentStep = storeCurrentStep !== undefined
                        ? storeCurrentStep
                        : (returned?.navigatorCurrentStep ?? fields.currentStep ?? this.currentStepId);
                    // The server's stored run. `fields.navigatorData` is only what this save sent
                    // (#48), so it is never a fallback for the store.
                    const navigatorData = returned?.navigatorData ?? null;
                    // Also this dialog's own copy, so its next edit starts from what is stored,
                    // including what the crew saved since it opened.
                    if (navigatorData && !fields.resetNavigator) {
                        try { this.pendingData = JSON.parse(navigatorData); } catch { /* keep the old copy */ }
                    }
                    this.store.dispatch(WorkOrdersActions.navigatorUpdatedLocally({
                        jobId,
                        eventId: this.eventId,
                        navigatorCurrentStep,
                        navigatorData,
                    }));

                    // Reach a navigator mounted in *this* tab explicitly (#46).
                    //
                    // The patch above updates the jobs slice only. A mounted navigator no longer reads
                    // its step from there, so without this an ops step change in the same browser
                    // would update the dialog and the workorders row while leaving the crew's screen
                    // where it was. Cross-machine overrides arrive through
                    // `remoteNavigatorStepOverride` instead; a reset goes through `navigatorReset`,
                    // which is dispatched separately in `reset()`.
                    if (fields.currentStep && fields.currentStep !== NAVIGATOR_STARTED_SENTINEL) {
                        let stepStartTime: number = Date.now();
                        try { stepStartTime = JSON.parse(navigatorData ?? '{}').stepStartTime ?? stepStartTime; } catch { /* now */ }
                        this.store.dispatch(quickJsActions.navigatorStepOverridden({
                            eventId: this.eventId,
                            stepId: fields.currentStep,
                            // The stored start of the step they land on: a re-opened step keeps its own (#53).
                            stepStartTime,
                        }));
                    }
                }

                onSuccess?.();
            },
            error: err => {
                this.saving = false;
                console.error('[UpdateNavigator] mutation failed:', err);
                onError?.(err);
            },
        });
    }

    private _extractEditableProps(
        props: Record<string, any>,
        override: Record<string, any>,
    ): Record<string, any> {
        const SKIP = new Set([
            'id', 'title', 'maxTime', 'location', 'undoTime',
            'confirmButton', 'confirmButtonEnabled', 'showNotes',
            'afterConfirm', 'jobNotes', 'eventNotes',
        ]);
        const result: Record<string, any> = {};
        for (const [key, val] of Object.entries(props)) {
            if (SKIP.has(key) || typeof val === 'function') continue;
            if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
                result[key] = override[key] ?? val;
            }
        }
        return result;
    }
}
